// src/components/admin/TenantCreator.jsx
import React, { useState } from 'react';
import { FiX, FiCheck, FiAlertTriangle, FiLock, FiDatabase, FiFolder } from 'react-icons/fi';
import { db, storage } from '../../config/firebase';
import { collection, doc, setDoc, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';

const TenantCreator = ({ isOpen, onClose, onTenantCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postcode: '',
      country: 'UK'
    },
    branding: {
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      logo: null
    },
    subscription: {
      plan: 'starter',
      maxUsers: 5,
      maxClients: 100,
      maxStorage: 1000 // MB
    },
    settings: {
      timezone: 'Europe/London',
      currency: 'GBP',
      dateFormat: 'DD/MM/YYYY',
      workingHours: {
        monday: { start: '09:00', end: '17:00', enabled: true },
        tuesday: { start: '09:00', end: '17:00', enabled: true },
        wednesday: { start: '09:00', end: '17:00', enabled: true },
        thursday: { start: '09:00', end: '17:00', enabled: true },
        friday: { start: '09:00', end: '17:00', enabled: true },
        saturday: { start: '09:00', end: '13:00', enabled: false },
        sunday: { start: '09:00', end: '13:00', enabled: false }
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});

  // Auto-generate slug from name
  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
    }));
  };

  // Validate tenant data
  const validateTenant = async () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Clinic name is required';
    if (!formData.slug.trim()) errors.slug = 'Clinic slug is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    
    // Check if slug is unique
    if (formData.slug) {
      const existingTenant = await getDocs(
        query(collection(db, 'tenants'), where('slug', '==', formData.slug))
      );
      if (!existingTenant.empty) {
        errors.slug = 'This clinic slug is already taken';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Create secure tenant with proper data isolation
  const createTenant = async () => {
    try {
      setLoading(true);
      
      // Validate first
      const isValid = await validateTenant();
      if (!isValid) {
        toast.error('Please fix validation errors');
        return;
      }

      const tenantId = formData.slug;
      const now = new Date().toISOString();
      
      // 1. Create tenant document
      const tenantDoc = {
        ...formData,
        id: tenantId,
        status: 'active',
        createdAt: now,
        updatedAt: now,
        lastActivity: now,
        dataIsolation: {
          firestoreCollections: [
            `tenants/${tenantId}/clients`,
            `tenants/${tenantId}/appointments`,
            `tenants/${tenantId}/staff`,
            `tenants/${tenantId}/invoices`,
            `tenants/${tenantId}/soapNotes`,
            `tenants/${tenantId}/waitlist`,
            `tenants/${tenantId}/rooms`,
            `tenants/${tenantId}/settings`
          ],
          storagePrefix: `tenants/${tenantId}/`,
          securityRules: {
            read: `resource.data.tenantId == '${tenantId}'`,
            write: `resource.data.tenantId == '${tenantId}' && request.auth != null`
          }
        }
      };

      await setDoc(doc(db, 'tenants', tenantId), tenantDoc);
      
      // 2. Create tenant-scoped collections with initial documents
      const collections = ['clients', 'appointments', 'staff', 'invoices', 'soapNotes', 'waitlist', 'rooms'];
      
      for (const collectionName of collections) {
        // Create a placeholder document to initialize the collection
        const placeholderDoc = {
          _placeholder: true,
          tenantId: tenantId,
          createdAt: now,
          message: `This is a placeholder document for ${collectionName} collection. It will be removed when real data is added.`
        };
        
        await setDoc(
          doc(db, 'tenants', tenantId, collectionName, '_placeholder'), 
          placeholderDoc
        );
      }
      
      // 3. Create tenant settings document
      const settingsDoc = {
        ...formData.settings,
        tenantId: tenantId,
        createdAt: now,
        updatedAt: now
      };
      
      await setDoc(
        doc(db, 'tenants', tenantId, 'settings', 'general'), 
        settingsDoc
      );
      
      // 4. Create storage folder structure (by uploading a placeholder file)
      const storageRef = ref(storage, `tenants/${tenantId}/.placeholder`);
      const placeholderBlob = new Blob(['This folder belongs to tenant: ' + tenantId], { type: 'text/plain' });
      await uploadBytes(storageRef, placeholderBlob);
      
      // 5. Create security audit log
      const auditLog = {
        action: 'tenant_created',
        tenantId: tenantId,
        details: {
          name: formData.name,
          slug: formData.slug,
          collectionsCreated: collections.length,
          storageInitialized: true
        },
        timestamp: now,
        createdBy: 'super_admin' // TODO: Get actual user ID
      };
      
      await setDoc(doc(db, 'audit_logs', `tenant_created_${tenantId}_${Date.now()}`), auditLog);
      
      toast.success(`Tenant "${formData.name}" created successfully with secure data isolation!`);
      
      // Call parent callback
      if (onTenantCreated) {
        onTenantCreated(tenantDoc);
      }
      
      // Reset form and close
      setFormData({
        name: '',
        slug: '',
        email: '',
        phone: '',
        address: { street: '', city: '', postcode: '', country: 'UK' },
        branding: { primaryColor: '#2563eb', secondaryColor: '#64748b', logo: null },
        subscription: { plan: 'starter', maxUsers: 5, maxClients: 100, maxStorage: 1000 },
        settings: {
          timezone: 'Europe/London',
          currency: 'GBP',
          dateFormat: 'DD/MM/YYYY',
          workingHours: {
            monday: { start: '09:00', end: '17:00', enabled: true },
            tuesday: { start: '09:00', end: '17:00', enabled: true },
            wednesday: { start: '09:00', end: '17:00', enabled: true },
            thursday: { start: '09:00', end: '17:00', enabled: true },
            friday: { start: '09:00', end: '17:00', enabled: true },
            saturday: { start: '09:00', end: '13:00', enabled: false },
            sunday: { start: '09:00', end: '13:00', enabled: false }
          }
        }
      });
      setStep(1);
      onClose();
      
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error('Failed to create tenant: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create New Clinic Tenant</h2>
            <p className="text-sm text-gray-600">Set up a new clinic with secure data isolation</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNum ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > stepNum ? <FiCheck className="h-4 w-4" /> : stepNum}
                </div>
                {stepNum < 3 && (
                  <div className={`w-12 h-1 mx-2 ${
                    step > stepNum ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Basic Info</span>
            <span>Configuration</span>
            <span>Security Review</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinic Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Downtown Physiotherapy Clinic"
                  />
                  {validationErrors.name && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinic Slug * <span className="text-gray-500">(auto-generated)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.slug ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="downtown-physiotherapy-clinic"
                  />
                  {validationErrors.slug && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.slug}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">This will be used for data isolation and URLs</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="admin@clinic.com"
                  />
                  {validationErrors.email && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+44 20 1234 5678"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Street Address"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, city: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.address.postcode}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, postcode: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Postcode"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Configuration & Branding</h3>
              
              {/* Branding */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Branding</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.branding.primaryColor}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, primaryColor: e.target.value }
                        }))}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.branding.primaryColor}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, primaryColor: e.target.value }
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="#2563eb"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.branding.secondaryColor}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, secondaryColor: e.target.value }
                        }))}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.branding.secondaryColor}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, secondaryColor: e.target.value }
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="#64748b"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Plan */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Subscription Plan</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['starter', 'professional', 'enterprise'].map((plan) => (
                    <div
                      key={plan}
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        subscription: { 
                          ...prev.subscription, 
                          plan,
                          maxUsers: plan === 'starter' ? 5 : plan === 'professional' ? 20 : 100,
                          maxClients: plan === 'starter' ? 100 : plan === 'professional' ? 500 : 2000,
                          maxStorage: plan === 'starter' ? 1000 : plan === 'professional' ? 5000 : 20000
                        }
                      }))}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        formData.subscription.plan === plan 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <h5 className="font-medium text-gray-900 capitalize">{plan}</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        {plan === 'starter' && '5 users, 100 clients, 1GB storage'}
                        {plan === 'professional' && '20 users, 500 clients, 5GB storage'}
                        {plan === 'enterprise' && '100 users, 2000 clients, 20GB storage'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Security & Data Isolation Review</h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <FiLock className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Data Isolation Guaranteed</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      This tenant will have completely isolated data with no cross-tenant access possible.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FiDatabase className="h-5 w-5 text-gray-600 mr-3" />
                    <span className="text-sm font-medium text-gray-900">Firestore Collections</span>
                  </div>
                  <span className="text-sm text-green-600">✓ Isolated</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FiFolder className="h-5 w-5 text-gray-600 mr-3" />
                    <span className="text-sm font-medium text-gray-900">File Storage</span>
                  </div>
                  <span className="text-sm text-green-600">✓ Isolated</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FiLock className="h-5 w-5 text-gray-600 mr-3" />
                    <span className="text-sm font-medium text-gray-900">Security Rules</span>
                  </div>
                  <span className="text-sm text-green-600">✓ Enforced</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Tenant Summary</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><strong>Name:</strong> {formData.name}</div>
                  <div><strong>Slug:</strong> {formData.slug}</div>
                  <div><strong>Email:</strong> {formData.email}</div>
                  <div><strong>Plan:</strong> {formData.subscription.plan}</div>
                  <div><strong>Storage Path:</strong> tenants/{formData.slug}/</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Back
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={createTenant}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <FiCheck className="h-4 w-4" />
                    <span>Create Tenant</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantCreator;
